import { motion } from 'framer-motion';
import { LockClosedIcon, CodeBracketIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import Hero from '../components/home/Hero';
import WhyModulo from '../components/home/WhyModulo';
import Services from '../components/home/Services';
import Features from '../components/home/Features';
import CommunityStats from '../components/home/CommunityStats';

function Home() {
  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <Hero />
      <WhyModulo />
      <Services />
      <Features />
      
      {/* Call to Action */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-b from-transparent via-indigo-500/20 to-transparent" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-t from-transparent via-indigo-500/20 to-transparent" />
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="text-center relative"
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [SECURE_ACCESS_POINT]
          </div>
          
          <div className="relative inline-block mb-8">
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear"
              }}
              className="absolute -inset-4 rounded-full bg-indigo-500/10 blur-xl"
            />
            <LockClosedIcon className="w-12 h-12 text-indigo-400 mx-auto mb-6" />
          </div>
          
          <h2 className="text-4xl font-bold mb-8 font-mono relative inline-block">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-indigo-400 to-indigo-500">
              Let us help you get going?
            </span>
            <div className="absolute -inset-1 border border-indigo-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity" />
          </h2>
          
          <Link to="/services/technology">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-indigo-500/20 rounded backdrop-blur-sm group-hover:bg-indigo-500/30 transition-colors" />
              <div className="relative px-8 py-4 font-mono text-indigo-400 border border-indigo-500/50 rounded 
                group-hover:border-indigo-400 transition-colors"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <span>Get Started</span>
                  <span className="font-mono text-xs opacity-60">[SECURE]</span>
                </span>
                
                {/* Corner decorations */}
                <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-indigo-500 group-hover:border-indigo-400 transition-colors" />
                <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-indigo-500 group-hover:border-indigo-400 transition-colors" />
              </div>
            </motion.button>
          </Link>
          
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [END_SECURE_ACCESS_POINT]
          </div>
        </motion.div>
      </section>

      <CommunityStats />

      {/* Developer Community */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.02)_1px,transparent_1px)] bg-[size:14px_24px]" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          {/* Header decoration */}
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [NETWORK::COMMUNITY_ACCESS]
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-mono mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                Join Our Developer Network
              </span>
            </h2>
            <p className="text-slate-400 font-mono max-w-2xl mx-auto">
              Connect with the elite network of privacy innovators. Build the future of secure applications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 to-purple-900/50 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                group-hover:border-indigo-500/40 transition-all duration-300" />
              
              <div className="relative p-8">
                <div className="flex items-center gap-4 mb-6">
                  <CodeBracketIcon className="w-8 h-8 text-indigo-400" />
                  <div className="font-mono text-xs text-indigo-500/60">ACCESS_CODE: DEV_001</div>
                </div>
                
                <h3 className="text-xl font-bold text-white font-mono mb-4">Start Building</h3>
                <p className="text-sm text-slate-400 font-mono mb-6">
                  Access our comprehensive resources and start building privacy-first applications.
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full relative group"
                >
                  <div className="absolute inset-0 bg-indigo-500/20 rounded backdrop-blur-sm group-hover:bg-indigo-500/30 transition-colors" />
                  <div className="relative px-6 py-3 font-mono text-indigo-400 border border-indigo-500/50 rounded 
                    group-hover:border-indigo-400 transition-colors flex items-center justify-center gap-2"
                  >
                    <span>Initialize Build</span>
                    <span className="text-xs opacity-60">[0x1F4]</span>
                  </div>
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 to-indigo-900/50 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                group-hover:border-indigo-500/40 transition-all duration-300" />
              
              <div className="relative p-8">
                <div className="flex items-center gap-4 mb-6">
                  <svg className="w-8 h-8 text-indigo-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                  </svg>
                  <div className="font-mono text-xs text-indigo-500/60">ACCESS_CODE: COM_002</div>
                </div>
                
                <h3 className="text-xl font-bold text-white font-mono mb-4">Join Discord</h3>
                <p className="text-sm text-slate-400 font-mono mb-6">
                  Connect with our community of privacy-focused developers and innovators.
                </p>
                
                <motion.a
                  href="https://discord.gg/MZ6Yxn8M"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full relative group block"
                >
                  <div className="absolute inset-0 bg-indigo-500/20 rounded backdrop-blur-sm group-hover:bg-indigo-500/30 transition-colors" />
                  <div className="relative px-6 py-3 font-mono text-indigo-400 border border-indigo-500/50 rounded 
                    group-hover:border-indigo-400 transition-colors flex items-center justify-center gap-2"
                  >
                    <span>Connect Network</span>
                    <span className="text-xs opacity-60">[0x2E8]</span>
                  </div>
                </motion.a>
              </div>
            </motion.div>
          </div>

          {/* Footer decoration */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [END::COMMUNITY_ACCESS]
          </div>
        </motion.div>
      </section>

    </div>
  );
}

export default Home;